/**
 * Runtime SEO helpers for the SPA.
 *
 * The app is a single HTML document that swaps views client-side. Only the
 * landing page and the privacy notice are intended to be indexed; scenarios,
 * onboarding, debrief, and survey contain research texts and participant
 * responses that MUST stay out of search engines. We flip <meta name="robots">
 * (and the document title) whenever we mount a new view.
 *
 * Canonical URL stays pointing at the site root: the SPA never changes URL,
 * so there is no per-view canonical to emit.
 */

export type PageKind =
  | 'landing'
  | 'privacy'
  | 'aboutResearch'
  | 'onboarding'
  | 'scenario'
  | 'outcome'
  | 'debrief'
  | 'survey'
  | 'withdraw'
  | 'replayConsent';

const PUBLIC_PAGES: ReadonlySet<PageKind> = new Set(['landing', 'privacy', 'aboutResearch']);

const DEFAULT_TITLE = 'Allot — a study of moral judgments in humanitarian aid allocation';
const PRIVATE_TITLE = 'Allot';

function ensureMeta(name: string): HTMLMetaElement {
  let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  return tag;
}

export function setPageSeo(kind: PageKind): void {
  const indexable = PUBLIC_PAGES.has(kind);
  const robots = ensureMeta('robots');
  robots.setAttribute(
    'content',
    indexable ? 'index,follow' : 'noindex,nofollow,noarchive',
  );
  document.title = indexable ? DEFAULT_TITLE : PRIVATE_TITLE;
}
